/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { addUserToSegment } from '../fn/segmentation/add-user-to-segment';
import { AddUserToSegment$Params } from '../fn/segmentation/add-user-to-segment';
import { createSegment } from '../fn/segmentation/create-segment';
import { CreateSegment$Params } from '../fn/segmentation/create-segment';
import { deleteSegment } from '../fn/segmentation/delete-segment';
import { DeleteSegment$Params } from '../fn/segmentation/delete-segment';
import { getSegment } from '../fn/segmentation/get-segment';
import { GetSegment$Params } from '../fn/segmentation/get-segment';
import { getSegments } from '../fn/segmentation/get-segments';
import { GetSegments$Params } from '../fn/segmentation/get-segments';
import { getSegmentsForUser } from '../fn/segmentation/get-segments-for-user';
import { GetSegmentsForUser$Params } from '../fn/segmentation/get-segments-for-user';
import { getUsersInSegment } from '../fn/segmentation/get-users-in-segment';
import { GetUsersInSegment$Params } from '../fn/segmentation/get-users-in-segment';
import { PagedResourcesSegment } from '../models/paged-resources-segment';
import { removeUserFromSegment } from '../fn/segmentation/remove-user-from-segment';
import { RemoveUserFromSegment$Params } from '../fn/segmentation/remove-user-from-segment';
import { Segment } from '../models/segment';
import { SegmentIdsResponse } from '../models/segment-ids-response';
import { SegmentResponse } from '../models/segment-response';
import { SegmentUsers } from '../models/segment-users';
import { updateSegment } from '../fn/segmentation/update-segment';
import { UpdateSegment$Params } from '../fn/segmentation/update-segment';

@Injectable({ providedIn: 'root' })
export class SegmentationService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getSegments()` */
  static readonly GetSegmentsPath = '/segments';

  /**
   * get all segments
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSegments()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSegments$Response(params: GetSegments$Params, context?: HttpContext): Observable<StrictHttpResponse<PagedResourcesSegment>> {
    return getSegments(this.http, this.rootUrl, params, context);
  }

  /**
   * get all segments
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getSegments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSegments(params: GetSegments$Params, context?: HttpContext): Observable<PagedResourcesSegment> {
    return this.getSegments$Response(params, context).pipe(
      map((r: StrictHttpResponse<PagedResourcesSegment>): PagedResourcesSegment => r.body)
    );
  }

  /** Path part for operation `createSegment()` */
  static readonly CreateSegmentPath = '/segments';

  /**
   * create a new segment
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createSegment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createSegment$Response(params: CreateSegment$Params, context?: HttpContext): Observable<StrictHttpResponse<SegmentResponse>> {
    return createSegment(this.http, this.rootUrl, params, context);
  }

  /**
   * create a new segment
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createSegment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createSegment(params: CreateSegment$Params, context?: HttpContext): Observable<SegmentResponse> {
    return this.createSegment$Response(params, context).pipe(
      map((r: StrictHttpResponse<SegmentResponse>): SegmentResponse => r.body)
    );
  }

  /** Path part for operation `addUserToSegment()` */
  static readonly AddUserToSegmentPath = '/segments/{segmentId}/users/{userId}';

  /**
   * add a user to a segment
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addUserToSegment()` instead.
   *
   * This method doesn't expect any request body.
   */
  addUserToSegment$Response(params: AddUserToSegment$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return addUserToSegment(this.http, this.rootUrl, params, context);
  }

  /**
   * add a user to a segment
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `addUserToSegment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  addUserToSegment(params: AddUserToSegment$Params, context?: HttpContext): Observable<void> {
    return this.addUserToSegment$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `removeUserFromSegment()` */
  static readonly RemoveUserFromSegmentPath = '/segments/{segmentId}/users/{userId}';

  /**
   * remove a user from a segment
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `removeUserFromSegment()` instead.
   *
   * This method doesn't expect any request body.
   */
  removeUserFromSegment$Response(params: RemoveUserFromSegment$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return removeUserFromSegment(this.http, this.rootUrl, params, context);
  }

  /**
   * remove a user from a segment
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `removeUserFromSegment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  removeUserFromSegment(params: RemoveUserFromSegment$Params, context?: HttpContext): Observable<void> {
    return this.removeUserFromSegment$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `getSegment()` */
  static readonly GetSegmentPath = '/segments/{segmentId}';

  /**
   * get a segment
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSegment()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSegment$Response(params: GetSegment$Params, context?: HttpContext): Observable<StrictHttpResponse<SegmentResponse>> {
    return getSegment(this.http, this.rootUrl, params, context);
  }

  /**
   * get a segment
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getSegment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSegment(params: GetSegment$Params, context?: HttpContext): Observable<SegmentResponse> {
    return this.getSegment$Response(params, context).pipe(
      map((r: StrictHttpResponse<SegmentResponse>): SegmentResponse => r.body)
    );
  }

  /** Path part for operation `updateSegment()` */
  static readonly UpdateSegmentPath = '/segments/{segmentId}';

  /**
   * update a segment
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateSegment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSegment$Response(params: UpdateSegment$Params, context?: HttpContext): Observable<StrictHttpResponse<Segment>> {
    return updateSegment(this.http, this.rootUrl, params, context);
  }

  /**
   * update a segment
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateSegment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSegment(params: UpdateSegment$Params, context?: HttpContext): Observable<Segment> {
    return this.updateSegment$Response(params, context).pipe(
      map((r: StrictHttpResponse<Segment>): Segment => r.body)
    );
  }

  /** Path part for operation `deleteSegment()` */
  static readonly DeleteSegmentPath = '/segments/{segmentId}';

  /**
   * Deletes a segment
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteSegment()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteSegment$Response(params: DeleteSegment$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return deleteSegment(this.http, this.rootUrl, params, context);
  }

  /**
   * Deletes a segment
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteSegment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteSegment(params: DeleteSegment$Params, context?: HttpContext): Observable<void> {
    return this.deleteSegment$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `getUsersInSegment()` */
  static readonly GetUsersInSegmentPath = '/segments/{segmentId}/users';

  /**
   * get users in a segment
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUsersInSegment()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUsersInSegment$Response(params: GetUsersInSegment$Params, context?: HttpContext): Observable<StrictHttpResponse<SegmentUsers>> {
    return getUsersInSegment(this.http, this.rootUrl, params, context);
  }

  /**
   * get users in a segment
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUsersInSegment$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUsersInSegment(params: GetUsersInSegment$Params, context?: HttpContext): Observable<SegmentUsers> {
    return this.getUsersInSegment$Response(params, context).pipe(
      map((r: StrictHttpResponse<SegmentUsers>): SegmentUsers => r.body)
    );
  }

  /** Path part for operation `getSegmentsForUser()` */
  static readonly GetSegmentsForUserPath = '/users/{userId}/segments';

  /**
   * get segments for a user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSegmentsForUser()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSegmentsForUser$Response(params: GetSegmentsForUser$Params, context?: HttpContext): Observable<StrictHttpResponse<SegmentIdsResponse>> {
    return getSegmentsForUser(this.http, this.rootUrl, params, context);
  }

  /**
   * get segments for a user
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getSegmentsForUser$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSegmentsForUser(params: GetSegmentsForUser$Params, context?: HttpContext): Observable<SegmentIdsResponse> {
    return this.getSegmentsForUser$Response(params, context).pipe(
      map((r: StrictHttpResponse<SegmentIdsResponse>): SegmentIdsResponse => r.body)
    );
  }

}
